import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useHistory } from 'react-router';
import useToast from '../customHooks/useToast';
import {
  GET_PROFILE,
} from '../graphql/admin_queries';
import { IAdmin } from '../interfaces/admin';
import useCustomQuery from '../network/useCustomQuery';

type GlobalState = {
  profile?: IAdmin;
  token?: string;
  profileLoading?: boolean;
  profileData?: { getAdminUser: { data: IAdmin } };
  shrinkBar?: boolean;
  setShrinkBar?:any;
  payfiProducts?: any;
  categories?: any;
  groupUsers?: any;
  userPermissions?:any;
};

const CONTEXT_ERROR = 'Global context not found. Have you wrapped your components with GlobalContext.Consumer?';
type Action =
  | { type: 'LOAD_PROFILE'; data: IAdmin }
  | { type: 'SET_TOKEN'; data: string }
  | { type: 'SET_STEP'; data: number }
  | { type: 'SET_POS'; data: number }
  | { type: 'SET_EMAIL_VERIFIED_TRUE'; data: any }
  | { type: 'RESET_STEP' }
  | { type: 'RESET_POS' }
  | { type: 'LOAD_PAYFI_PRODUCT', data: any }
  | { type: 'LOAD_CATEGORY', data: any }|{ type: 'LOAD_GROUP_USERS', data: any }|{type:'SET_USER_PERMISSIONS', data:[]}

type dispatch = {
  setToken: (token: string) => void;
  setUser: (arg: any) => void;
  clearUser: () => void;
  get_profile: () => void;
  get_profile_in_app: () => void;
  setEmailVerifiedTrue: () => void;
  setUserPermissions: (arg:[]) => void;

};

const GlobalStateContext = createContext<GlobalState | undefined>(undefined);
const GlobalDispatchContext = createContext<dispatch>({
  setToken: () => {
    throw new Error(CONTEXT_ERROR);
  },
  setUser: () => {
    throw new Error(CONTEXT_ERROR);
  },
  clearUser: () => {
    throw new Error(CONTEXT_ERROR);
  },
  get_profile: () => {
    throw new Error(CONTEXT_ERROR);
  },
  get_profile_in_app: () => {
    throw new Error(CONTEXT_ERROR);
  },
  setEmailVerifiedTrue: () => {
    throw new Error(CONTEXT_ERROR);
  },
  setUserPermissions: () => {
    throw new Error(CONTEXT_ERROR);
  },
});

function reducer(prevState: GlobalState, action: Action) {
  switch (action.type) {
  case 'LOAD_PROFILE':
    return {
      ...prevState,
      profile: { ...action.data, isEmailVerified: true },
    };
  case 'SET_TOKEN':
    return { ...prevState, token: action.data };
  case 'SET_EMAIL_VERIFIED_TRUE':
    return {
      ...prevState,
      profile: { ...prevState?.profile, isEmailVerified: true },
    };
  case 'LOAD_PAYFI_PRODUCT':
    return { ...prevState, payfiProducts: action.data };
  case 'LOAD_CATEGORY':
    return { ...prevState, categories: action.data };
  case 'LOAD_GROUP_USERS':
    return { ...prevState, groupUsers: action.data };
  case 'SET_USER_PERMISSIONS':
    return { ...prevState, userPermissions: action.data };
  default:
    return prevState;
  }
}

const AdminGlobalProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const defaults = {
    profile: {},
    token: '',
  };
  const Toast = useToast();
  const [state, dispatch] = useReducer(reducer, {
    ...defaults,
  });
  const { push } = useHistory();
  const [shrinkBar, setShrinkBar] = useState(true);
  const [getProfileQuery, profileLoading, profileData] = useCustomQuery({
    query: GET_PROFILE,
    onError: (err: any) => {
      Toast.error('Failed to get profile details');
      if (err) push('/');
    },
    onData: () => {},
  });

  const [getProfileInAppQuery, , profileInAppData] = useCustomQuery({
    query: GET_PROFILE,
    onError: () => {},
    onData: () => {},
  });

  const get_profile = async () => {
    try {
      await getProfileQuery();
    } catch (e) {
      Toast.error('Failed to get profile details');
      if (e && e.graphQLErrors && e.graphQLErrors.length > 0) {
        console.log(e.graphQLErrors[0].message);
      } else {
        console.log('Something went wrong');
      }
    }
  };

  const get_profile_in_app = async () => {
    try {
      await getProfileInAppQuery();
    } catch (e) {
      Toast.error('Failed to get profile details');
      if (e && e.graphQLErrors && e.graphQLErrors.length > 0) {
        console.log(e.graphQLErrors[0].message);
      } else {
        console.log('Something went wrong');
      }
    }
  };

  const setEmailVerifiedTrue = () => {
    dispatch({ type: 'SET_EMAIL_VERIFIED_TRUE', data: {} });
  };

  useEffect(() => {
    if (profileData) {
      if (profileData?.getAdminUser?.id) {
        const user = profileData?.getAdminUser;
        localStorage.setItem('user', JSON.stringify(user));
        dispatch({ type: 'LOAD_PROFILE', data: user });
        const getPermissionList = user?.role?.permissions?.map((list:any) => list?.name);

        dispatch({ type: 'SET_USER_PERMISSIONS', data: getPermissionList });
      } else {
        Toast.error('Failed to get profile details');
        push('/');
      }
    }
  }, [profileData]);

  useEffect(() => {
    if (profileInAppData) {
      if (profileInAppData?.getAdminUser?.id) {
        const user = profileInAppData?.getAdminUser;
        localStorage.setItem('user', JSON.stringify(user));
        dispatch({ type: 'LOAD_PROFILE', data: user });
      }
    }
  }, [profileInAppData]);
  // actions
  const GlobalDispatch = useMemo(
    () => ({
      setToken: async (token: string) => {
        localStorage.setItem('token', token);
        dispatch({ type: 'SET_TOKEN', data: token });
      },
      setUser: async (user: any) => {
        localStorage.setItem('user', JSON.stringify(user));
        dispatch({ type: 'LOAD_PROFILE', data: user });
      },
      clearUser: async () => {
        localStorage.clear();
        dispatch({ type: 'LOAD_PROFILE', data: {} });
        dispatch({ type: 'SET_TOKEN', data: '' });
      },
      get_profile: async () => get_profile(),
      get_profile_in_app: async () => get_profile_in_app(),
      setEmailVerifiedTrue: async () => setEmailVerifiedTrue(),
      setUserPermissions: async (permissions: []) => {
        dispatch({ type: 'SET_USER_PERMISSIONS', data: permissions });
      },
    }),
    [],
  );

  return (
    <GlobalDispatchContext.Provider value={GlobalDispatch}>
      <GlobalStateContext.Provider
        value={{
          ...state, profileLoading, profileData, shrinkBar, setShrinkBar,
        }}
      >
        {children}
      </GlobalStateContext.Provider>
    </GlobalDispatchContext.Provider>
  );
};

function useGlobalState(): GlobalState {
  const context = useContext(GlobalStateContext);
  if (context === undefined) {
    throw new Error('useGlobalState must be used with a AdminGlobalProvider');
  }
  return context;
}

function useGlobalDispatch(): dispatch {
  const context = useContext(GlobalDispatchContext);
  if (context === undefined) {
    throw new Error(
      'useGlobalDispatch must be used with a AdminGlobalProvider',
    );
  }
  return context;
}

export { useGlobalDispatch, useGlobalState, AdminGlobalProvider };
